/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { When } from "react-if";
import {
  Description,
  Heading,
  Label,
  Spacer,
  Text,
  theme,
} from "@nordcloud/gnui";
import { CloudProviderType } from "~/generated/graphql";
import { Fieldset, InputField, ContactsInput } from "~/components";
import { ERROR_TEXT, SUCCESS_TEXT } from "~/constants";
import { showError, showSuccess } from "~/services/toast";
import { StepContainer } from "~/views/accounts/AddAccount/components/StepContainer";
import {
  KUBERNETES_TEXT,
  VMWARE_TEXT,
} from "~/views/accounts/AddAccount/constants";
import { AccountContacts, AccountParams } from "../types";
import { schemaCredsHybrid } from "./validators";

enum FIELDS {
  ownerId = "ownerId",
  contactIds = "contactIds",
  clusterName = "clusterName",
}

type FormData = {
  [FIELDS.ownerId]: string;
  [FIELDS.contactIds]: string;
  [FIELDS.clusterName]: string;
};

type Props = {
  onAddAccountSubmit: SubmitHandler<FormData>;
  accountParams: AccountParams;
  additionalSettings: AccountContacts;
  setAdditionalSettings: (settings: AccountContacts) => void;
};

export function HybridAccountForm({
  onAddAccountSubmit,
  accountParams,
  additionalSettings,
  setAdditionalSettings,
}: Props) {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schemaCredsHybrid),
    defaultValues: {
      [FIELDS.clusterName]: accountParams.name,
    },
  });

  const HYBRID_LABELS =
    accountParams.providerType === CloudProviderType.Vmware
      ? VMWARE_TEXT
      : KUBERNETES_TEXT;

  React.useEffect(() => {
    register(FIELDS.ownerId);
    register(FIELDS.contactIds);
  }, [register]);

  return (
    <form id="HYBRID-cred-form" onSubmit={handleSubmit(onAddAccountSubmit)}>
      <Spacer height="1rem" />
      <Heading level={3} marginBottom="spacing03">
        Account Details
      </Heading>
      <Text color={theme.color.text.text02}>{HYBRID_LABELS.titleInfo}</Text>
      <Spacer height="2rem" />
      <StepContainer counter={1}>
        <Text>{HYBRID_LABELS.firstStepInfo}</Text>
        <InputField
          label={HYBRID_LABELS.clusterLabel ?? ""}
          {...register(FIELDS.clusterName, { required: true })}
          error={errors.clusterName}
          required
        />
        <Fieldset>
          <Label name={HYBRID_LABELS.clusterOwner} required />
          <Controller
            name={FIELDS.ownerId}
            control={control}
            defaultValue={additionalSettings.ownerId}
            render={({ field: { value, onChange } }) => (
              <ContactsInput
                name="ownerId"
                value={[value]}
                placeholder="Select owner"
                status={errors.ownerId && "error"}
                onChange={(id: string) => {
                  try {
                    setAdditionalSettings({
                      ...additionalSettings,
                      ownerId: id,
                    });
                    showSuccess(SUCCESS_TEXT.accountOwnerAdded);
                    onChange(id);
                  } catch {
                    showError(ERROR_TEXT.default);
                  }
                }}
              />
            )}
          />
          <When condition={!!errors.ownerId}>
            <Description>{HYBRID_LABELS.ownerError}</Description>
          </When>
        </Fieldset>
      </StepContainer>
      <StepContainer counter={2}>
        <Text>{HYBRID_LABELS.secondStepInfo}</Text>
        <Text color={theme.color.text.text02}>
          {HYBRID_LABELS.instructions}
        </Text>
      </StepContainer>
    </form>
  );
}
